import { useQuery, useReactiveVar } from "@apollo/client";
import {
  GetRoleFrameworkStatusQuery,
  GetRoleFrameworkStatusQueryVariables,
} from "types/graphql-schema";

import { currentOrganizationVar, isAdminVar } from "@cache/cache";

import getRoleFrameworkStatusQuery from "../../graphql/get-role-framework-status-query";

export type RoleFrameworkStatus = "empty" | "exists";

// Whether to offer building ("empty") or extending ("exists") the role
// framework with Topicflow AI, or null when the offer shouldn't be shown.
export const useRoleFrameworkStatus = (
  organizationId: number,
): RoleFrameworkStatus | null => {
  const organization = useReactiveVar(currentOrganizationVar);
  // Global and people admins: the only ones Ask AI offers the role framework
  // tools to (backend `offers_role_framework`).
  const isAdmin = useReactiveVar(isAdminVar);
  // Ask AI operates in the current organization. Never launch another organization's setup there.
  const eligible =
    organization?.id === organizationId &&
    !!organization.featureFlags.askAi &&
    isAdmin;
  const { data, loading, error } = useQuery<
    GetRoleFrameworkStatusQuery,
    GetRoleFrameworkStatusQueryVariables
  >(getRoleFrameworkStatusQuery, {
    variables: { organizationId },
    skip: !eligible,
    // `careerTracks` is cached under `keyArgs: ["organizationId"]` with a merge that
    // replaces `edges`, so writing this `first: 1` result would clobber the job
    // ladder's list on this same page. Keep the status check out of the cache.
    fetchPolicy: "no-cache",
  });
  if (!eligible || loading || error || !data) return null;
  return !data.careerTracks.edges.length && !data.competencies.edges.length
    ? "empty"
    : "exists";
};
